import { LockMark, LockMarks, ManifestLockPageDTO } from "../models/LockDTO";

/**
 * Page id → lock mark from the manifest's `locked` / `lock` fields (OrbitalPlan/18 §3 rule 6). A page with `lock` carries its own lock;
 * a `locked` page without one inherits the nearest lock on its parent chain. Servers without locks send neither field: no marks.
 */
export function buildLockMarks(pages: ManifestLockPageDTO[] | undefined): LockMarks {
    const marks: LockMarks = {};
    if (!pages?.length) return marks;

    const byId = new Map<number, ManifestLockPageDTO>();
    for (const p of pages) byId.set(p.id, p);

    for (const page of pages) {
        if (!page.locked && !page.lock) continue;
        if (page.lock) {
            marks[page.id] = { own: true, lockPageId: page.id, lockTitle: page.lock.title || page.title };
            continue;
        }

        // nearest ancestor carrying the lock; if the chain is cut (a page the caller cannot see), keep the topmost locked page found
        let lockPageId = page.id;
        let lockTitle = "";
        const seen = new Set<number>([page.id]);
        let parentId = page.parentId ?? undefined;
        while (parentId !== undefined && parentId !== null && !seen.has(parentId)) {
            seen.add(parentId);
            const parent = byId.get(parentId);
            if (!parent) break;
            if (parent.lock) {
                lockPageId = parent.id;
                lockTitle = parent.lock.title || parent.title;
                break;
            }
            if (parent.locked) lockPageId = parent.id;
            parentId = parent.parentId ?? undefined;
        }
        if (!lockTitle && lockPageId !== page.id) lockTitle = byId.get(lockPageId)?.title ?? "";
        marks[page.id] = { own: false, lockPageId, lockTitle };
    }
    return marks;
}

/** Hover text of a lock badge. */
export function lockBadgeTitle(mark: LockMark): string {
    if (mark.own) return "Locked page";
    return mark.lockTitle ? `Locked through “${mark.lockTitle}”` : "Locked through a parent page";
}
